import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/layout/Header";
import { fetchStats } from "@/lib/api";
import { formatTime } from "@/lib/game/numberPlacement";
import {
  Trophy, Calendar, Infinity as InfinityIcon, Clock, Timer, Lightbulb, Flame, Award, Play,
} from "lucide-react";

const StatCard = ({ icon, label, value, accent, testid, delay }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white border-2 border-ink rounded-2xl shadow-brutal p-5"
  >
    <div
      className="grid place-items-center w-11 h-11 rounded-lg border-2 border-ink mb-3"
      style={{ backgroundColor: accent }}
    >
      {icon}
    </div>
    <div className="font-mono font-extrabold text-3xl tabular-nums leading-none">{value}</div>
    <div className="mt-1.5 text-xs font-black uppercase tracking-[0.15em] text-ink/50 font-display">
      {label}
    </div>
  </motion.div>
);

export default function StatsPage() {
  const { t, deviceId } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats(deviceId)
      .then(setStats)
      .catch(() => setStats(null));
  }, [deviceId]);

  const nd = t("no_data");
  const s = stats || {};
  const bt = s.best_time_ms ? formatTime(s.best_time_ms) : nd;
  const at = s.avg_time_ms ? formatTime(s.avg_time_ms) : nd;

  return (
    <div className="min-h-screen bg-world">
      <Header />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-lg bg-sunny border-2 border-ink shadow-brutal-sm">
            <Trophy className="w-6 h-6 text-ink" strokeWidth={2.5} />
          </span>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{t("stats_title")}</h1>
            <p className="text-sm font-medium text-ink/50">{t("stats_sub")}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <StatCard testid="stat-total" delay={0.02} accent="#FF5A5F33"
            icon={<Award className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("total_completed")} value={s.total_completed ?? 0} />
          <StatCard testid="stat-daily" delay={0.05} accent="#FFC04333"
            icon={<Calendar className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("daily_completed")} value={s.daily_completed ?? 0} />
          <StatCard testid="stat-endless" delay={0.08} accent="#4CAF5033"
            icon={<InfinityIcon className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("endless_completed")} value={s.endless_completed ?? 0} />
          <StatCard testid="stat-hints" delay={0.11} accent="#FFC04333"
            icon={<Lightbulb className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("hints_used")} value={s.hints_used ?? 0} />
          <StatCard testid="stat-best-time" delay={0.14} accent="#FF5A5F33"
            icon={<Clock className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("best_time")} value={bt} />
          <StatCard testid="stat-avg-time" delay={0.17} accent="#4CAF5033"
            icon={<Timer className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("avg_time")} value={at} />
          <StatCard testid="stat-current-streak" delay={0.2} accent="#FF5A5F33"
            icon={<Flame className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("current_streak")} value={`${s.current_streak ?? 0} ${t("days")}`} />
          <StatCard testid="stat-longest-streak" delay={0.23} accent="#FFC04333"
            icon={<Flame className="w-5 h-5 text-ink" strokeWidth={2.5} />}
            label={t("longest_streak")} value={`${s.longest_streak ?? 0} ${t("days")}`} />
        </div>

        <div className="mt-8">
          <button
            data-testid="stats-play-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-coral text-white border-2 border-ink font-display font-bold shadow-brutal hover:-translate-y-0.5 transition-transform"
          >
            <Play className="w-5 h-5" strokeWidth={2.5} /> {t("start_playing")}
          </button>
        </div>
      </section>
    </div>
  );
}
